// Interactive survey results dashboard, reused by the SurveyResults route page,
// the participant PulseConnect "Results" tab and the PB admin results tab.
import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../services/supabase';
import {
  C, PulseSurvey, PulseQuestion, PulseResponse, PulseAnswer,
  normalizeQuestion, fmtDateTime, toCSV, downloadCSV, summarizeThemes,
} from './pulse';
import { card, btnGhost, btnPrimary, LoadingScreen, Banner, QuestionResult } from './pulseUI';

interface ProfileLite { id: string; full_name: string | null; email: string | null; }

const DIMS = [
  { key: 'firm_strategy' as const, label: 'Strategy' },
  { key: 'firm_aum_range' as const, label: 'AUM Range' },
  { key: 'firm_hq_region' as const, label: 'HQ Region' },
];

export default function ResultsDashboard({ surveyId, isPB, embedded }: {
  surveyId: string; isPB: boolean; embedded?: boolean;
}) {
  const [survey, setSurvey] = useState<PulseSurvey | null>(null);
  const [questions, setQuestions] = useState<PulseQuestion[]>([]);
  const [responses, setResponses] = useState<PulseResponse[]>([]);
  const [answers, setAnswers] = useState<PulseAnswer[]>([]);
  const [profiles, setProfiles] = useState<Record<string, ProfileLite>>({});
  const [assignmentCount, setAssignmentCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [filters, setFilters] = useState<Record<string, string[]>>({ firm_strategy: [], firm_aum_range: [], firm_hq_region: [] });
  const [tableView, setTableView] = useState(false);
  const [aiThemes, setAiThemes] = useState<Record<string, string>>({});
  const [aiLoading, setAiLoading] = useState<string | null>(null);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [surveyId]);

  async function load() {
    setLoading(true); setErr('');
    const { data: s } = await supabase.from('pulse_surveys').select('*').eq('id', surveyId).maybeSingle();
    if (!s) { setErr('Survey not found or you do not have access to it.'); setLoading(false); return; }
    setSurvey(s as PulseSurvey);

    const { data: qs } = await supabase.from('pulse_questions').select('*').eq('survey_id', surveyId).order('order_index');
    const questionList = (qs || []).map(normalizeQuestion);
    setQuestions(questionList);

    const { data: resp } = await supabase.from('pulse_responses').select('*').eq('survey_id', surveyId).eq('is_complete', true);
    const respList = (resp as PulseResponse[]) || [];
    setResponses(respList);

    if (respList.length) {
      const { data: ans } = await supabase.from('pulse_answers').select('*')
        .in('response_id', respList.map(r => r.id));
      setAnswers((ans as PulseAnswer[]) || []);
    } else {
      setAnswers([]);
    }

    const { count } = await supabase.from('pulse_assignments').select('*', { count: 'exact', head: true }).eq('survey_id', surveyId);
    setAssignmentCount(count || respList.length);

    if (isPB && respList.length) {
      const ids = Array.from(new Set(respList.map(r => r.profile_id).filter(Boolean))) as string[];
      if (ids.length) {
        const { data: profs } = await supabase.from('profiles').select('id, full_name, email').in('id', ids);
        const map: Record<string, ProfileLite> = {};
        (profs || []).forEach((p: any) => { map[p.id] = p; });
        setProfiles(map);
      }
    }
    setLoading(false);
  }

  // Filter option lists derived from the actual response values present.
  const dimOptions = useMemo(() => {
    const out: Record<string, string[]> = {};
    DIMS.forEach(d => {
      out[d.key] = Array.from(new Set(responses.map(r => r[d.key]).filter(Boolean) as string[])).sort();
    });
    return out;
  }, [responses]);

  const filteredResponses = useMemo(() => responses.filter(r =>
    DIMS.every(d => {
      const sel = filters[d.key];
      return !sel.length || (r[d.key] && sel.includes(r[d.key] as string));
    })
  ), [responses, filters]);

  const filteredResponseIds = useMemo(() => new Set(filteredResponses.map(r => r.id)), [filteredResponses]);
  const filteredAnswers = useMemo(() => answers.filter(a => filteredResponseIds.has(a.response_id)), [answers, filteredResponseIds]);
  const answersByQuestion = useMemo(() => {
    const m: Record<string, PulseAnswer[]> = {};
    filteredAnswers.forEach(a => { (m[a.question_id] ||= []).push(a); });
    return m;
  }, [filteredAnswers]);

  const nameFor = (rid: string) => {
    const r = responses.find(x => x.id === rid);
    const p = r?.profile_id ? profiles[r.profile_id] : null;
    return p?.full_name || p?.email || 'Respondent';
  };

  const anyFilter = DIMS.some(d => filters[d.key].length);
  const resetFilters = () => setFilters({ firm_strategy: [], firm_aum_range: [], firm_hq_region: [] });
  const toggleFilter = (key: string, val: string) => setFilters(f => ({
    ...f, [key]: f[key].includes(val) ? f[key].filter(v => v !== val) : [...f[key], val],
  }));

  const completionRate = assignmentCount ? Math.round((responses.length / assignmentCount) * 100) : 0;

  async function exportCSV() {
    if (!survey) return;
    if (isPB) {
      // Individual, non-anonymous rows.
      const header = ['Respondent', 'Email', 'Strategy', 'AUM Range', 'HQ Region', 'Completed', ...questions.map(q => q.question_text)];
      const rows: (string | number | null)[][] = [header];
      filteredResponses.forEach(r => {
        const p = r.profile_id ? profiles[r.profile_id] : null;
        const line: (string | number | null)[] = [
          p?.full_name || 'Respondent', p?.email || '', r.firm_strategy, r.firm_aum_range, r.firm_hq_region, fmtDateTime(r.completed_at),
        ];
        questions.forEach(q => {
          const a = answers.find(x => x.response_id === r.id && x.question_id === q.id);
          line.push(serializeAnswer(q, a));
        });
        rows.push(line);
      });
      downloadCSV(`${slug(survey.title)}-responses.csv`, toCSV(rows));
    } else {
      // Aggregate only.
      const rows: (string | number | null)[][] = [['Question', 'Answer / Item', 'Count / Average']];
      questions.forEach(q => {
        const qa = answersByQuestion[q.id] || [];
        aggregateRows(q, qa).forEach(([k, v]) => rows.push([q.question_text, k, v]));
      });
      downloadCSV(`${slug(survey.title)}-summary.csv`, toCSV(rows));
    }
  }

  async function runAI(q: PulseQuestion) {
    setAiLoading(q.id);
    try {
      const texts = (answersByQuestion[q.id] || []).map(a => a.answer_text || '').filter(Boolean);
      const summary = await summarizeThemes(q.question_text, texts);
      setAiThemes(t => ({ ...t, [q.id]: summary }));
    } catch (e: any) {
      setAiThemes(t => ({ ...t, [q.id]: `AI summary failed: ${e.message || e}` }));
    } finally {
      setAiLoading(null);
    }
  }

  if (loading) return <LoadingScreen label="Loading results…" />;
  if (err) return <div style={{ padding: embedded ? 0 : '28px 32px' }}><Banner type="err" text={err} /></div>;
  if (!survey) return null;

  return (
    <div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      {/* print header, only visible when printing */}
      <div className="pulse-print-only" style={{ display: 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
          <span style={{ fontFamily: 'Georgia, serif', fontSize: '18px' }}>Altveno</span>
          <span style={{ color: C.gold, fontSize: '10px', fontWeight: 700, letterSpacing: '1px' }}>PULSECONNECT</span>
        </div>
      </div>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap', marginBottom: '18px' }}>
        <div>
          <h2 style={{ fontFamily: 'Georgia, serif', fontSize: embedded ? '19px' : '23px', color: C.black, fontWeight: 400, margin: '0 0 6px', letterSpacing: '-0.3px' }}>{survey.title}</h2>
          <div style={{ display: 'flex', gap: '18px', flexWrap: 'wrap', fontSize: '12.5px', color: C.textSec }}>
            <span><strong style={{ color: C.navy }}>{responses.length}</strong> {responses.length === 1 ? 'response' : 'responses'}</span>
            {isPB && <span>Completion rate <strong style={{ color: C.navy }}>{completionRate}%</strong> ({responses.length}/{assignmentCount})</span>}
            {anyFilter && <span style={{ color: C.gold, fontWeight: 600 }}>Showing {filteredResponses.length} of {responses.length}</span>}
          </div>
        </div>
        <div className="pulse-no-print" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {isPB && <button onClick={() => setTableView(v => !v)} style={btnGhost}>{tableView ? 'Chart view' : 'Response table'}</button>}
          <button onClick={() => window.print()} style={btnGhost}>Export PDF</button>
          <button onClick={exportCSV} style={btnPrimary}>Export CSV</button>
        </div>
      </div>

      {/* Filter panel */}
      <div className="pulse-no-print" style={{ ...card, padding: '14px 16px', marginBottom: '18px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
          <span style={{ fontSize: '11px', fontWeight: 700, color: C.muted, letterSpacing: '0.6px', textTransform: 'uppercase' }}>Filter by demographic</span>
          {anyFilter && <button onClick={resetFilters} style={{ ...btnGhost, padding: '4px 12px', fontSize: '11px' }}>Reset filters</button>}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px' }}>
          {DIMS.map(d => (
            <div key={d.key}>
              <div style={{ fontSize: '10.5px', fontWeight: 700, color: C.textSec, marginBottom: '6px' }}>{d.label}</div>
              {dimOptions[d.key].length === 0 ? (
                <span style={{ fontSize: '11.5px', color: C.mutedLt }}>No data</span>
              ) : (
                <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                  {dimOptions[d.key].map(opt => {
                    const on = filters[d.key].includes(opt);
                    return (
                      <button key={opt} onClick={() => toggleFilter(d.key, opt)} style={{
                        padding: '4px 9px', borderRadius: '3px', fontSize: '11px', cursor: 'pointer', fontFamily: 'system-ui', fontWeight: 500,
                        border: `1px solid ${on ? C.navy : C.border}`, background: on ? C.navy : C.white, color: on ? C.white : C.textSec,
                      }}>{on ? '✓ ' : ''}{opt}</button>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {filteredResponses.length === 0 ? (
        <div style={{ ...card, padding: '40px', textAlign: 'center', fontSize: '13px', color: C.muted }}>
          {responses.length === 0 ? 'No completed responses yet.' : 'No responses match the current filters.'}
        </div>
      ) : isPB && tableView ? (
        <ResponseTable questions={questions} responses={filteredResponses} answers={answers} nameFor={nameFor} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {questions.map((q, i) => (
            <div key={q.id} className="pulse-result-card" style={{ ...card, padding: '18px 20px' }}>
              <div style={{ display: 'flex', gap: '10px', marginBottom: '14px' }}>
                <span style={{ fontSize: '11px', fontWeight: 700, color: C.gold, fontFamily: 'Georgia, serif', flexShrink: 0 }}>Q{i + 1}</span>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: C.text, lineHeight: 1.4 }}>{q.question_text}</div>
                  {q.description && <div style={{ fontSize: '12px', color: C.muted, marginTop: '3px' }}>{q.description}</div>}
                </div>
              </div>
              <QuestionResult question={q} answers={answersByQuestion[q.id] || []} isPB={isPB} nameFor={nameFor} />

              {isPB && q.question_type === 'free_text' && (answersByQuestion[q.id] || []).some(a => a.answer_text) && (
                <div className="pulse-no-print" style={{ marginTop: '14px', borderTop: `1px solid ${C.borderLt}`, paddingTop: '12px' }}>
                  {aiThemes[q.id] ? (
                    <div style={{ background: 'rgba(201,168,76,0.06)', border: `1px solid rgba(201,168,76,0.3)`, borderRadius: '4px', padding: '12px 14px' }}>
                      <div style={{ fontSize: '10.5px', fontWeight: 700, color: C.gold, letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: '6px' }}>AI Summary</div>
                      <div style={{ fontSize: '12.5px', color: C.textSec, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{aiThemes[q.id]}</div>
                    </div>
                  ) : (
                    <button onClick={() => runAI(q)} disabled={aiLoading === q.id} style={{ ...btnGhost, fontSize: '11.5px' }}>
                      {aiLoading === q.id ? 'Analysing themes…' : '✦ Summarise themes with AI'}
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="pulse-print-only" style={{ display: 'none', marginTop: '20px', fontSize: '10px', color: C.mutedLt }}>
        Generated from the Altveno platform. Confidential.
      </div>
    </div>
  );
}

// ── Response table (PB only) ─────────────────────────────────────────────────
function ResponseTable({ questions, responses, answers, nameFor }: {
  questions: PulseQuestion[]; responses: PulseResponse[]; answers: PulseAnswer[]; nameFor: (id: string) => string;
}) {
  return (
    <div style={{ ...card, overflowX: 'auto' }}>
      <table style={{ borderCollapse: 'collapse', width: '100%', fontFamily: 'system-ui', fontSize: '11.5px' }}>
        <thead>
          <tr>
            {['Respondent', 'Strategy', 'AUM', 'Region', ...questions.map((_, i) => `Q${i + 1}`)].map((h, i) => (
              <th key={i} style={{ textAlign: 'left', padding: '9px 10px', fontSize: '10px', color: C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', borderBottom: `1px solid ${C.border}`, background: C.surface, whiteSpace: 'nowrap' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {responses.map(r => (
            <tr key={r.id}>
              <td style={{ padding: '8px 10px', borderBottom: `1px solid ${C.borderLt}`, fontWeight: 600, color: C.navy, whiteSpace: 'nowrap' }}>{nameFor(r.id)}</td>
              <td style={{ padding: '8px 10px', borderBottom: `1px solid ${C.borderLt}`, color: C.textSec }}>{r.firm_strategy || '-'}</td>
              <td style={{ padding: '8px 10px', borderBottom: `1px solid ${C.borderLt}`, color: C.textSec }}>{r.firm_aum_range || '-'}</td>
              <td style={{ padding: '8px 10px', borderBottom: `1px solid ${C.borderLt}`, color: C.textSec }}>{r.firm_hq_region || '-'}</td>
              {questions.map(q => {
                const a = answers.find(x => x.response_id === r.id && x.question_id === q.id);
                return <td key={q.id} style={{ padding: '8px 10px', borderBottom: `1px solid ${C.borderLt}`, color: C.text, maxWidth: '200px' }}>{serializeAnswer(q, a) || '-'}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Serialisation helpers ────────────────────────────────────────────────────
function serializeAnswer(q: PulseQuestion, a: PulseAnswer | undefined): string {
  if (!a) return '';
  switch (q.question_type) {
    case 'multiple_choice':
    case 'free_text':
      return a.answer_text || '';
    case 'rating':
    case 'nps':
      return a.answer_number !== null && a.answer_number !== undefined ? String(a.answer_number) : '';
    case 'multi_select':
      return (a.answer_options || []).join('; ');
    case 'ranking':
      return (a.answer_options || []).map((o, i) => `${i + 1}. ${o}`).join('; ');
    case 'matrix':
    case 'grid':
      return Object.entries(a.answer_matrix || {}).map(([r, c]) => `${r}: ${c}`).join('; ');
    default:
      return '';
  }
}

function aggregateRows(q: PulseQuestion, answers: PulseAnswer[]): [string, string | number][] {
  switch (q.question_type) {
    case 'multiple_choice': {
      const counts: Record<string, number> = {};
      answers.forEach(a => { if (a.answer_text) counts[a.answer_text] = (counts[a.answer_text] || 0) + 1; });
      return Object.entries(counts).map(([k, v]) => [k, v]);
    }
    case 'multi_select': {
      const counts: Record<string, number> = {};
      answers.forEach(a => (a.answer_options || []).forEach(o => { counts[o] = (counts[o] || 0) + 1; }));
      return Object.entries(counts).map(([k, v]) => [k, v]);
    }
    case 'rating':
    case 'nps': {
      const vals = answers.map(a => a.answer_number).filter((n): n is number => n !== null && n !== undefined);
      const avg = vals.length ? (vals.reduce((s, n) => s + n, 0) / vals.length).toFixed(2) : '0';
      return [['Average', avg], ['Responses', vals.length]];
    }
    case 'ranking': {
      const totals: Record<string, { sum: number; n: number }> = {};
      answers.forEach(a => (a.answer_options || []).forEach((item, idx) => { (totals[item] ||= { sum: 0, n: 0 }); totals[item].sum += idx + 1; totals[item].n += 1; }));
      return Object.entries(totals).map(([k, v]) => [k, (v.sum / v.n).toFixed(2)] as [string, string]).sort((a, b) => parseFloat(a[1]) - parseFloat(b[1]));
    }
    case 'matrix':
    case 'grid': {
      const colScore: Record<string, number> = {};
      q.matrix_columns.forEach((c, i) => { colScore[c] = i + 1; });
      const rowAvg: Record<string, { sum: number; n: number }> = {};
      answers.forEach(a => Object.entries(a.answer_matrix || {}).forEach(([r, c]) => { if (colScore[c]) { (rowAvg[r] ||= { sum: 0, n: 0 }); rowAvg[r].sum += colScore[c]; rowAvg[r].n += 1; } }));
      return Object.entries(rowAvg).map(([k, v]) => [k, (v.sum / v.n).toFixed(2)]);
    }
    case 'free_text':
      return answers.filter(a => a.answer_text).map((a, i) => [`Response ${i + 1}`, a.answer_text || '']);
    default:
      return [];
  }
}

function slug(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40) || 'survey';
}
